import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";

// Import del servizio Keycloak
import {
  initKeycloak,
  getToken,
  isAuthenticated,
  updateToken,
  logout,
} from "./services/keycloakService";

import "animate.css";

import Buefy from "buefy";
import { ToastProgrammatic as Toast } from "buefy";
import VueCropper from "vue-cropper";

import VueBreakpointMixin from "vue-breakpoint-mixin";
Vue.mixin(VueBreakpointMixin);

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";

library.add(fas, far, fab);

Vue.use(Buefy);
Vue.use(VueCropper);

import VueMatomo from "vue-matomo";

Vue.config.productionTip = false;

// Sentry
import * as Sentry from "@sentry/vue";
import Package from "../package.json";

import "./index.css";

const mount = () => {
  new Vue({
    router,
    store,
    render: (h) => h(App),
  }).$mount("#app");
};

const init = async () => {
  try {
    const config = (await axios.get("/config.json", { baseURL: "" })).data;

    // Rendi disponibile config in Vue
    Vue.prototype.$config = config;

    // Start configure Matomo
    Vue.use(VueMatomo, {
      host: config.matomo.host,
      siteId: config.matomo.siteId,
      trackerFileName: "matomo",
      router: router,
      enableLinkTracking: true,
      requireConsent: false,
      trackInitialView: true,
      disableCookies: false,
      requireCookieConsent: false,
      enableHeartBeatTimer: false,
      heartBeatTimerInterval: 15,
      debug: false,
      userId: undefined,
      cookieDomain: undefined,
      domains: undefined,
      preInitActions: [],
      trackSiteSearch: false,
      crossOrigin: undefined,
    });
    // End configure Matomo

    axios.defaults.baseURL = config.apiUrl;

    // Sentry
    if (config.sentry.enabled === true) {
      Sentry.init({
        app: Vue,
        dsn: config.sentry.dsn,
        environment: config.sentry.environment,
        release: `${Package.name}@${Package.version}`,
        tracesSampleRate: config.sentry.tracesSampleRate,
        tracePropagationTargets: config.sentry.tracePropagationTargets,
        integrations: [
          Sentry.browserTracingIntegration({ router }),
          Sentry.replayIntegration(),
        ],
        replaysSessionSampleRate: config.sentry.replaysSessionSampleRate,
        replaysOnErrorSampleRate: config.sentry.replaysOnErrorSampleRate,
      });
    }

    // Inizializzazione Keycloak tramite il servizio
    const keycloakInitialized = await initKeycloak(config.keycloak, {
      onLoad: "login-required",
      checkLoginIframe: false,
    });

    if (keycloakInitialized) {
      // Setup dell'interceptor per axios con refresh token
      axios.interceptors.request.use(
        async (axiosConfig) => {
          // Aggiorna il token prima di ogni richiesta
          if (isAuthenticated()) {
            try {
              await updateToken();
            } catch {
              logout();
            }
          }

          if (axiosConfig.headers && getToken() !== undefined) {
            if (
              axiosConfig.baseURL === config.picksUserUrl ||
              axiosConfig.baseURL === config.sisNotifyUrl
            ) {
              axiosConfig.headers["Authorization"] = `Bearer ${getToken()}`;
            } else {
              axiosConfig.headers["X-Auth-Token"] = getToken();
            }
          }
          return axiosConfig;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      // Tentativo di registrazione utente
      try {
        await axios.post(`${config.apiUrl}/user/register`);
      } catch {
        Toast.open({
          duration: 3000,
          message: "Registration failed",
          position: "is-top-right",
          type: "is-danger",
        });

        mount();
        router.push({ name: "fallback" });
        return;
      }
    }

    mount();
  } catch {
    Toast.open({
      duration: 3000,
      message: "Authentication failed",
      position: "is-top-right",
      type: "is-danger",
    });

    mount();
    router.push({ name: "fallback" });
  }
};

init();
